import { ChangeDetectorRef, Inject, LOCALE_ID, NgZone, OnDestroy } from "@angular/core";
import { OnInit } from "@angular/core";
import { Component } from "@angular/core";
import {
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
} from "@azure/msal-angular";
import {
  AuthenticationResult,
  EndSessionRequest,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import { BehaviorSubject, firstValueFrom, Subject } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { WelcomeComponent } from "./components/welcome/welcome.component";
import { UserProfileService } from "./_services/userprofile.service";
import { Roles } from "./constants/Roles";
import { Userprofile } from "./_models/Userprofile";
import { AppConstants } from "./constants/constants";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { BaseComponent } from "./components/base/base-component.component";
import { MatDialog, MatDialogState } from "@angular/material/dialog";
import { BroadcastChannel } from "broadcast-channel";
import { TimeoutWarningDialogComponent } from "./views/timeout-warning-dialog/timeout-warning-dialog.component";
import { Idle, EventTargetInterruptOptions, DocumentInterruptSource } from '@ng-idle/core';
import { UserDisplayHeader } from "./_models/UserDisplayHeader";
import { PublicUserSessionService } from "./_services/public-user-session.service";
import { APP_CONFIG } from "./lib/angular/injection-tokens";
import { IAppConfig } from "./lib/config/app-config";
import { Store } from "@ngrx/store";
import { AllowUnload, SetCurrentRoute } from "./_store/navigation/navigation.actions";
import { selectRouteOnDirty } from "./_store/navigation/navigation.selectors";
import { routes } from "./views/admin/admin-routing.module";
import { UserSessionService } from "./_services/user-session.service";
import { RoleService } from "./_services/role.service";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { ConfirmTransferDialogComponent } from "./components/dialog/confirm-transfer-dialog/confirm-transfer-dialog.component";
import { selectOwnUserProfile } from "./_store/user-profile/user-profile.selectors";
import { SetUserProfile } from "./_store/user-profile/user-profile.actions";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [
    MatPaginatorIntl
  ]
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  isDarkTheme: boolean = false;
  title = "WEST-Client";
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  logInSubscription: any;
  siteLanguageCode: string = this.localeId;
  isAdmin: boolean;
  isSystemAdmin: boolean;
  isTutor: boolean = false;
  roleEnum = Roles;
  accountRoles: Roles[] = [
    Roles.ContentAdministrator,
    Roles.Guest,
    Roles.Learner,
    Roles.OrganisationalAdministrator,
    Roles.SystemAdministrator,
    Roles.Tutor,
    Roles.GlobalAdministrator
  ];
  prevUrl = sessionStorage.getItem(AppConstants.PREV_PATH);
  broadcastChannel!: BroadcastChannel;
  userHeaderDetails: UserDisplayHeader = new UserDisplayHeader();
  routeOnDirty: string = null;
  isDisplayScreenLoader: boolean = false;
  onStartUpScreenLoader: boolean = false;
  initialLoadSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  constructor(
    private idle: Idle,
    private router: Router,
    public dialog: MatDialog,
    @Inject(LOCALE_ID) protected localeId: string,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    @Inject(APP_CONFIG) private config: IAppConfig,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private publicUserSessionService: PublicUserSessionService,
    private userProfileService: UserProfileService,
    private zone: NgZone,
    private store: Store,
    private userSessionService: UserSessionService,
    private roleService: RoleService,
    private cd: ChangeDetectorRef,
    private _matPaginatorIntl: MatPaginatorIntl
  ) {
    super(); 
  }

  ngOnInit(): void {
    this.goBackNavigation();
    this.getPreviousUrl();
    this.isIframe = window !== window.parent && !window.opener;
    this.loginDisplay = false;
    this.broadcastChannel = new BroadcastChannel(
      AppConstants.WEST_BROADCAST_CHANNEL
    );

    /**
     * You can subscribe to MSAL events as shown below. For more info,
     * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
     */
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) =>
            status === InteractionStatus.None ||
            status === InteractionStatus.HandleRedirect
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        if (this.loginDisplay) {
          this.getOwnUserProfile();
        }
      });

    this.onInitIdle();

    //Logout from other open tab or window
    this.broadcastChannel.onmessage = (status) => {
      if (status === AppConstants.ON_SESSION_TIMEOUT) {
        this.zone.run(() => {
          this.onSessionLogout();
        });
      } else if (status === AppConstants.ON_USER_LOGOUT) {
        this.zone.run(() => {
          this.logout();
        });
      }
    };

    const onUserDetailsUpdate$ =
      this.userProfileService.onUserDetailsUpdate$.subscribe((data) => {
        if (data) {
          this.updateUserHeaderDetails();
        }
      });

    this.store.select(selectRouteOnDirty)
      .pipe(takeUntil(this.destroy$))
      .subscribe((route: string) => {
        this.routeOnDirty = route;
      }
    );
    

    this.addSubscription(onUserDetailsUpdate$);

    const sub$ = this.userSessionService.onDisplayLoader$.subscribe((data: boolean) => {
      this.zone.run(()=>{
        if(!data)
        {
          setTimeout(()=>{
            this.isDisplayScreenLoader = data;
          }, 6000);
          return;
        }
        this.isDisplayScreenLoader = data;
      });
    });
    this.addSubscription(sub$);

    this.userSessionService.onStartUpDisplayLoader$.pipe(takeUntil(this.destroy$))
      .subscribe((loader: boolean) => {
        this.onStartUpScreenLoader = loader;
    });

    this._matPaginatorIntl.itemsPerPageLabel = AppConstants.ITEMS_PER_PAGE_LABEL;
    this._matPaginatorIntl.firstPageLabel = AppConstants.FIRST_PAGE_LABEL;
    this._matPaginatorIntl.previousPageLabel = AppConstants.PREV_PAGE_LABEL;
    this._matPaginatorIntl.nextPageLabel = AppConstants.NEXT_PAGE_LABEL;
    this._matPaginatorIntl.lastPageLabel = AppConstants.LAST_PAGE_LABEL;

    this.selectorOwnprofile();
  }

  onInitIdle(): void{

    /**
     * Subscriptions for idle callbacks
     * 
     * -onTimeoutSub$
     * -timeoutWarningSub$
     * -idleEndSub$
     */
    const onTimeoutSub$ = this.idle.onTimeout.subscribe(() => {
      this.store.dispatch(AllowUnload({value: true}));
      this.onSessionLogout();
      this.broadcastChannel.postMessage(AppConstants.ON_SESSION_TIMEOUT);
    });
    this.addSubscription(onTimeoutSub$);

    const timeoutWarningSub$ = this.idle.onTimeoutWarning.subscribe((data) => {
      this.displayTimeoutWarningModal(data);
    });
    this.addSubscription(timeoutWarningSub$);

    const idleEndSub$ = this.idle.onIdleEnd.subscribe(() => {
      const warningDialogExist = this.dialog.getDialogById(
        AppConstants.SESSION_TIMEOUT_WARNING_ID
      );

      if (
        warningDialogExist &&
        warningDialogExist.getState() === MatDialogState.OPEN
      ) {
        this.zone.run(() => {
          warningDialogExist.close();
        });
      }
      this.idle.watch();
    });
    this.addSubscription(idleEndSub$);
  }

  selectorOwnprofile(): void {
    this.store.select(selectOwnUserProfile)
    .pipe(takeUntil(this.destroy$))
    .subscribe((result) => {
      this.checkRecentTransferRequest(result);
    });
  }

  checkRecentTransferRequest(result: Userprofile): void {
    if(result) {
      if(result.recentTransferRequest != null && result.roleId == Roles.Learner && result.recentTransferRequest.transferId != 0 ) {
        if(result.recentTransferRequest.completedDate == null) {
          if(!this.dialog.getDialogById(AppConstants.CONFIRM_TRANSFER_DIALOG_ID)) {
            this.dialog.open(ConfirmTransferDialogComponent, {
              id: AppConstants.CONFIRM_TRANSFER_DIALOG_ID,
              disableClose: true,
              closeOnNavigation: false,
              data: result.recentTransferRequest
            });
          }
        }
      }
    }
  }

  getPreviousUrl(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        sessionStorage.setItem(AppConstants.PREV_PATH, this.prevUrl);
        this.prevUrl = event.url;
        if (!this.initialLoadSubject$.value) {
          this.store.dispatch(SetCurrentRoute({ value: event.url }));
        }
        this.initialLoadSubject$.next(false);
      });
  }

  goBackNavigation() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.navigationTrigger == AppConstants.POPSTATE) {
          if (
            this.router.url.includes(AppConstants.LEARNING_PLAN_PAGE) ||
            this.router.url.includes(AppConstants.LEARNER_COURSE_PAGE) ||
            this.router.url.includes(AppConstants.DASHBOARD_PAGE)
          ) {
            sessionStorage.setItem(AppConstants.NEXT_PATH, null);
            this.router.navigateByUrl(AppConstants.DASHBOARD_PAGE);
          }

          if (event.url.includes(AppConstants.CONTENT_PLAYER_PAGE)) {
            sessionStorage.setItem(AppConstants.NEXT_PATH, null);
            this.router.navigateByUrl(AppConstants.DASHBOARD_PAGE);
          }

          if (this.router.url == AppConstants.SCREENER_PAGE) {
            sessionStorage.setItem(AppConstants.NEXT_PATH, null);
            this.router.navigateByUrl(AppConstants.WELCOME_PAGE);
          }

          if (
            this.router.url == AppConstants.WELCOME_PAGE &&
            this.loginDisplay == false
          ) {
            sessionStorage.setItem(AppConstants.NEXT_PATH, null);
            this.router.navigateByUrl(AppConstants.WELCOME_PAGE);
          }

          if(this.router.url.includes(AppConstants.ADMIN_PAGE)){
              sessionStorage.setItem(AppConstants.NEXT_PATH, null);
              this.router.navigateByUrl(
                (AppConstants.NON_REDIRECTABLE_ROUTES.filter(x => event.url.includes(x)).length > 0) ? 
                (this.routeOnDirty) ?? this.getParentRoute(event.url) : 
                (this.routeOnDirty) ?? event.url
              );
          }

          if(this.router.url.includes(AppConstants.SLASH + AppConstants.TUTOR_PAGE + 
            AppConstants.SLASH + AppConstants.ADD_OFFLINE_ACTIVITY) || 
            this.router.url.includes(AppConstants.SLASH + AppConstants.TUTOR_PAGE + 
              AppConstants.SLASH + AppConstants.EDIT_OFFLINE_ACTIVITY)) 
          {
            sessionStorage.setItem(AppConstants.NEXT_PATH, null);
            this.router.navigateByUrl(event.url);
          }

          if(this.router.url.includes(AppConstants.TUTOR_PAGE)){
            sessionStorage.setItem(AppConstants.NEXT_PATH, null);
            this.router.navigateByUrl(event.url, {
              replaceUrl: true,
              skipLocationChange: false
            });
          }
        }
      });
  }

  getParentRoute(url: string): string {
    var parentRoute = routes[0].children.find(x => url.includes(x.path)).path;
    switch(parentRoute) {
      case AppConstants.USERS_PAGE:
        return AppConstants.ADMIN_DASHBOARD_LEARNERS_PAGE;
      case AppConstants.ORG_AND_CENTRE_PAGE:
        return AppConstants.ADMIN_DASHBOARD_LIST_ORG_PAGE;
      default:
        return "";
    }
  }

  async getOwnUserProfile(): Promise<void> {
    const userProfile: Userprofile = await firstValueFrom(this.userProfileService
      .getOwnUserProfile$());

    this.store.dispatch(SetUserProfile({userProfile: userProfile}));

    const adminRoleIds: number[] = (await firstValueFrom(this.roleService.getAdministratorRoles$())).map(r => r.id);
    if (adminRoleIds?.indexOf(Roles.SystemAdministrator) < 0) {
      adminRoleIds.push(Roles.SystemAdministrator);
    }

    this.isAdmin = (adminRoleIds?.indexOf(userProfile.roleId) > -1);
    this.isSystemAdmin = userProfile.roleId === Roles.SystemAdministrator;
    this.isTutor = userProfile.roleId === Roles.Tutor;
    this.cd.detectChanges();

    this.userHeaderDetails = this.getUserHeaderDetails(userProfile);

    this.onProcessConfigSessionTimeout(userProfile);
  }

  async updateUserHeaderDetails(): Promise<void> {
    const userProfile: Userprofile = await firstValueFrom(this.userProfileService
      .getOwnUserProfile$());

    this.userHeaderDetails = this.getUserHeaderDetails(userProfile);
  }

  setLoginDisplay(): void {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    super.ngOnDestroy();
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
  }

  onActivate(component): void {
    if (component instanceof WelcomeComponent) {
      if (!this.loginDisplay) {
        this.logInSubscription = component.loggingin_
          .pipe(switchMap(() => this.publicUserSessionService.clearSession$()))
          .subscribe(() => this.login());
      }
    }
  }

  onDeactivate(component): void {
    if (this.logInSubscription && component instanceof WelcomeComponent)
      this.logInSubscription.unsubscribe();
  }

  changePassword(): void {
    try {
      let changePassRequest: RedirectRequest | PopupRequest = {
        authority: this.config.b2cPolicies.authorities.changePassword,
        scopes: [this.config.protectedResourceDefaultScope],
      };

      if (this.siteLanguageCode == AppConstants.WELSH_LANG_CODE) {
        changePassRequest.extraQueryParameters = {
          ui_locales: AppConstants.WELSH_GB_LANG_CODE,
        };
      } else if (this.siteLanguageCode == AppConstants.DEFAULT_LANG_CODE) {
        changePassRequest.extraQueryParameters = {
          ui_locales: AppConstants.DEFAULT_LANG_CODE,
        };
      }
      this.authService.loginRedirect(changePassRequest);
    } catch (err) {
      console.error(err);
    }
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest): void {
    try {
      let authRequestConfig: RedirectRequest;
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
        if (this.msalGuardConfig.authRequest) {
          this.authService
            .loginPopup({
              ...this.msalGuardConfig.authRequest,
              ...userFlowRequest,
            } as PopupRequest)
            .subscribe((response: AuthenticationResult) => {
              this.authService.instance.setActiveAccount(response.account);
            });
        } else {
          this.authService
            .loginPopup(userFlowRequest)
            .subscribe((response: AuthenticationResult) => {
              this.authService.instance.setActiveAccount(response.account);
            });
        }
      } else {
        if (this.msalGuardConfig.authRequest) {
          authRequestConfig = {
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as RedirectRequest;
          if (this.siteLanguageCode == AppConstants.WELSH_LANG_CODE) {
            authRequestConfig.extraQueryParameters = {
              ui_locales: AppConstants.WELSH_GB_LANG_CODE,
            };
          } else if (this.siteLanguageCode == AppConstants.DEFAULT_LANG_CODE) {
            authRequestConfig.extraQueryParameters = {
              ui_locales: AppConstants.DEFAULT_LANG_CODE,
            };
          }
          this.authService.loginRedirect(authRequestConfig);
        } else {
          this.authService.loginRedirect(userFlowRequest);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  onProcessLogout(): void {
    const sub$ = this.publicUserSessionService.clearSession$().subscribe(() => {
      this.logout();
      this.broadcastChannel.postMessage(AppConstants.ON_USER_LOGOUT);
    });
    this.addSubscription(sub$);
  }

  logout(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.idle.stop();
    this.idle.clearInterrupts();

    this.authService.logout();   
  }

  displayTimeoutWarningModal(seconds: number): void {
    const warningDialogExist = this.dialog.getDialogById(
      AppConstants.SESSION_TIMEOUT_WARNING_ID
    );
    if (!warningDialogExist) {
      this.dialog.open(TimeoutWarningDialogComponent, {
        id: AppConstants.SESSION_TIMEOUT_WARNING_ID,
        data: {
          seconds,
        },
      });
    }
  }

  onProcessConfigSessionTimeout(userProfile: Userprofile): void {
    if (
      userProfile &&
      userProfile.roleId &&
      this.accountRoles.some((role) => role === userProfile.roleId &&
      !this.idle.isRunning()
      )
    ) {
        this.idle.setIdle(this.config.sessionTimeoutIdle);
        this.idle.setTimeout(this.config.sessionTimeoutCountdown);
        this.idle.setInterrupts(this.createDefaultInterruptSources());
        this.idle.watch();
    }
  }

  onSessionLogout(): void {
    this.idle.stop();
    this.idle.clearInterrupts();
    sessionStorage.clear();
    localStorage.clear();
    if (!this.authService) {
      return;
    }

    const currentAccount = this.authService.instance?.getActiveAccount();

    const request: EndSessionRequest = {
      account: currentAccount
    };

    this.authService?.logoutRedirect(request);
  }

  getUserHeaderDetails(userProfile: Userprofile): UserDisplayHeader {
    let details = new UserDisplayHeader();
    details.completeName =
      userProfile?.firstName && userProfile?.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`
        : "";
    details.email = userProfile?.email ?? "";
    return details;
  }

  private createDefaultInterruptSources(options?: EventTargetInterruptOptions) {
    return [
      new DocumentInterruptSource(
        AppConstants.DOCUMENT_INTERRUPT_CONFIG,
        options
      ),
    ];
  }

  checkIfMsalSignedInPage(): boolean {
    if(window.location.href.indexOf(AppConstants.MSAL_SIGNED_IN_PAGE) !== -1) {
      return true;
    }

    return false;
  }
}
